import { appleApprove, appleValidate, innerSearch } from 'api/Api';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import styled from 'styled-components';
import { findImage } from 'utils/Utils';
import PayverseHeader from 'app/components/PayverseHeader';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { usePaymentState } from 'payment_state/PaymentStateContext';

const ApplePayScreen = () => {
  const searchParams = useLocation().search;
  const paramTid = new URLSearchParams(searchParams).get('tid');
  const paramAmount = new URLSearchParams(searchParams).get('approvalAmount');
  const paramCurrency = new URLSearchParams(searchParams).get(
    'approvalCurrency',
  );
  const paramDescriptor = new URLSearchParams(searchParams).get('descriptor');
  const paramOrderType = new URLSearchParams(searchParams).get('orderType');
  const paramBrandList = new URLSearchParams(searchParams).get('cardBrandList');
  const paramMerchantCapabilities = new URLSearchParams(searchParams).get(
    'merchantCapabilities',
  );

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setPaymentState, setShopifyState } = usePaymentState();
  const [supportNetworks, setSupportNetworks] = useState<string[]>([]);
  const [merchantCapabilities, setMerchantCapabilities] = useState<string[]>(
    [],
  );

  const Parser = (String: string): string[] => {
    if (!String) return [];
    return String.split('|').filter(Boolean);
  };

  useEffect(() => {
    if (paramBrandList) {
      const networks = Parser(paramBrandList);
      setSupportNetworks(networks);
    }
  }, [paramBrandList]);

  useEffect(() => {
    if (paramMerchantCapabilities) {
      const capabilities = Parser(paramMerchantCapabilities);
      setMerchantCapabilities(capabilities);
    }
  }, [paramMerchantCapabilities]);

  const applePayment = async () => {
    if (!(window as any).ApplePaySession) {
      alert('Apple Pay is not supported on this browser.');
      return;
    }

    const paymentRequest = {
      /* 국가 코드 */
      countryCode: 'KR',
      /* 통화 코드 */
      currencyCode: paramCurrency,
      /*
       * 판매자가 지원하는 결제 네트워크(국가 별 브랜드 상이)
       * - amex, chinaUnionPay, discover, interac, jcb, masterCard, privateLabel, visa
       * - 참조 : https://developer.apple.com/documentation/applepayontheweb/applepaypaymentrequest/supportednetworks
       */

      supportedNetworks: supportNetworks,
      /*
       * 판매자가 지원하는 결제 수단
       * - supports3DS(필수)
       * - supportsCredit(선택) : 신용 카드로 분류된 거래만 허용
       * - supportsDebit(선택) : 직불 카드로 분류된 거래만 허용
       * - supportsEMV(선택) : 중국 UnionPay 거래를 지원하는 경우에만 포함
       * - 참조 : https://developer.apple.com/documentation/applepayontheweb/applepaypaymentrequest/merchantcapabilities
       */
      merchantCapabilities: merchantCapabilities,
      /* 지불을 나타내는 항목
       * - 참조 : https://developer.apple.com/documentation/applepayontheweb/applepaypaymentrequest/total
       */
      total: {
        label: paramDescriptor /* 업체명 */,
        amount: paramAmount /* 금액 */,
      },
    };
    /* 애플 페이 세션 생성(Version 3) */
    const session = new (window as any).ApplePaySession(3, paymentRequest);
    /* 1. 애플 페이 가맹점 인증(검증) 단계(필수) */
    session.onvalidatemerchant = async event => {
      try {
        const response = await appleValidate({
          tid: paramTid,
          validationUrl: event.validationURL,
          descriptor: paramDescriptor,
          domain: window.location.hostname,
          identifier:
            process.env.REACT_APP_ENV === 'production'
              ? 'merchant.payverseglobal.com'
              : 'merchant.com.payverse.sandbox',
        });

        const resultData = JSON.parse(response.data.data);
        const resultStatus = response.data.resultStatus;

        if (resultStatus === 'DECLINE') {
          if (isMobile) {
            transactResult.mutate();
          } else {
            window.close();
          }
        } else {
          session.completeMerchantValidation(resultData);
        }
      } catch (err) {
        console.error('Merchant validation failed', err);
        // 세션 강제 종료
        session.abort();
      }
    };
    /* 2. 결제 승인 시 처리 단계(필수) */
    session.onpaymentauthorized = async event => {
      const paymentToken = event.payment.token; // 암호화 된 결제 토큰
      try {
        const response = await appleApprove({
          tid: paramTid,
          token: paymentToken,
        });
        const resultStatus = response.data.resultStatus;
        // 결제 UI 종료
        session.completePayment(
          resultStatus === 'SUCCESS'
            ? (window as any).ApplePaySession.STATUS_SUCCESS
            : (window as any).ApplePaySession.STATUS_FAILURE,
        );

        // 결과처리 주문등록/일반
        if (paramOrderType && paramOrderType === 'order') {
          if (isMobile) {
            navigate(`/mobile/applepay/result?tid=${paramTid}`);
          } else {
            navigate(`/pc/applepay/result?tid=${paramTid}`);
          }
        } else {
          if (resultStatus === 'SUCCESS') {
            if (isMobile) {
              transactResult.mutate();
            } else {
              window.close();
            }
          } else {
            if (isMobile) navigate('/error');
            else {
              window.close();
            }
          }
        }
      } catch (err) {
        console.error('Payment failed', err);
        // 결제 UI 종료
        session.completePayment((window as any).ApplePaySession.STATUS_FAILURE);
      }
    };

    session.oncancel = event => {
      console.log('Payment cancelled:', event);
    };
    /* 애플 페이 시트 표시 */
    session.begin();
  };

  const updatePaymentState = infoData => {
    setPaymentState(prevState => ({
      ...prevState,
      requestCurrency: infoData.display.requestCurrency,
      requestAmount: infoData.display.requestAmount,
      reqDate: infoData.display.responseDatetime,
      orderId: infoData.response.orderId,
      resultCode: infoData.response.resultCode,
      resultMessage: infoData.response.resultMessage,
      mallReserved: infoData.response.mallReserved,
      billKey: infoData.response.billKey,
      schemeGroup: infoData.response.schemeGroup,
      schemeCode: infoData.response.schemeCode,
    }));
  };

  // InnerSearch 실행
  const transactStateSet = useMutation(() => innerSearch(paramTid), {
    onSuccess: data => {
      // PaymentState Set
      updatePaymentState(data.data);
    },
    onError: error => {
      // setLoading(false);
      navigate('/error');
    },
  });

  // InnerSearch 실행
  const transactResult = useMutation(() => innerSearch(paramTid), {
    onSuccess: data => {
      // InnerSearch 결과처리
      if (data.data.returnUrl.includes('shopifyConfirm')) {
        setShopifyState(data.data.response);
        navigate('/shopifyConfirm');
      } else {
        payResult(data);
        // 상태코드 예외처리
        if (
          data.data.response.resultStatus === null ||
          data.data.response.resultStatus === undefined
        ) {
          navigate('/error');
        }
      }
    },
    onError: error => {
      // setLoading(false);
      navigate('/error');
    },
  });

  // 결제 결과 PaymentState set 및 결과화면 처리
  const payResult = data => {
    // 결과상태
    const resultStatus = String(data.data.response.resultStatus).toLowerCase();
    // InnerSearch 결과 값 PaymentState 설정
    setPaymentState(prevState => ({
      ...prevState,
      resultCode: data.data.response.resultCode,
      resultMessage: data.data.response.resultMessage,
      tid: data.data.response.tid,
      orderId: data.data.response.orderId,
      mallReserved: data.data.response.mallReserved,
      billKey: data.data.response.billKey,
      returnUrl: data.data.returnUrl,
      descriptor: data.data.display.descriptor,
      schemeGroup: data.data.response.schemeGroup,
      schemeCode: data.data.response.schemeCode,
    }));
    // Status에 따른 Paging 처리
    switch (resultStatus) {
      case 'success':
        navigate('/success');
        break;
      case 'decline':
        navigate('/decline');
        break;
      case 'processing':
        navigate('/processing');
        break;
      case 'pending':
        navigate('/pending');
        break;
      case 'failed':
        navigate('/failed');
        break;
      default:
        navigate('/error');
    }
  };

  // PaymentState set을 위한 effect
  useEffect(() => {
    if (paramTid) {
      setPaymentState(prevState => ({
        ...prevState,
        tid: paramTid,
      }));
      transactStateSet.mutate();
    }
  }, [paramTid]);

  return (
    <Outer>
      <Header>
        <PayverseHeader />
        <PriceBox>
          <span>{t('pay_amount')}</span>
          <span>
            {paramAmount} {paramCurrency}
          </span>
        </PriceBox>
      </Header>
      <Container>
        <CardBox>
          <img
            src={findImage(`schemeList/GO/APPLEPAY-GLOBAL.png`)}
            width={50}
          />
        </CardBox>
        <h3>{t('descriptor')}</h3>
        <InfoBox>{paramDescriptor}</InfoBox>
      </Container>
      <Footer>
        <span>{t('agree_purchase')}</span>
        <button onClick={applePayment}>
          <img src={findImage(`apple-pay.svg`)} width={50} />
        </button>
      </Footer>
    </Outer>
  );
};

export default ApplePayScreen;

const Outer = styled.div`
  width: 100%;
  height: 100dvh;
  box-sizing: border-box;
  position: relative;
`;

const Header = styled.header`
  width: 100%;
  box-shadow: 0 3px 3px 0 #cfcfcf40;
  box-sizing: border-box;
`;
const PriceBox = styled.div`
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  span {
    font-size: 18px;
    font-weight: 600;
  }
  span:first-child {
    color: black;
  }
  span:nth-child(2) {
    color: #3c62dd;
  }
`;

const Footer = styled.div`
  box-sizing: border-box;
  padding: 20px;
  position: absolute;
  z-index: 10;
  left: 0;
  bottom: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  span {
    color: #757575;
    font-size: 16px;
  }
  button {
    width: 100%;
    height: 60px;
    background-color: black;
    border-radius: 4px;
    color: white;
  }
`;

const Container = styled.div`
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  h3 {
    font-size: 16px;
    margin-bottom: 4px;
    font-weight: 500;
    color: #212121;
  }
`;

const CardBox = styled.div`
  width: 100%;
  height: 50px;
  border-radius: 6px;
  border: 2px solid #f5f5f5;
  padding-left: 22px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
`;

const InfoBox = styled.div`
  width: 100%;
  height: 50px;
  border-radius: 6px;
  border: 2px solid #f5f5f5;
  display: flex;
  align-items: center;
  padding-left: 22px;
  box-sizing: border-box;
  background-color: #eeeeee;
  color: #bdbdbd;
  margin-bottom: 16px;
`;
